import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Image as ImageIcon, FileText, MapPin,
  Sparkles, Images, Wallet, Inbox, Settings, LogOut, ExternalLink, Plug,
  MessageSquareQuote, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_DARK } from "@/lib/brand";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/hero", label: "Hero Section", icon: ImageIcon },
  { to: "/admin/about", label: "About", icon: FileText },
  { to: "/admin/location", label: "Location", icon: MapPin },
  { to: "/admin/amenities", label: "Amenities", icon: Sparkles },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/pricing", label: "Pricing", icon: Wallet },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/blog", label: "Blog", icon: BookOpen },
  { to: "/admin/bookings", label: "Bookings", icon: Inbox },
  { to: "/admin/integrations", label: "Integrations", icon: Plug },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-earth-50 flex">
      <aside
        data-testid="admin-sidebar"
        className="w-64 bg-white border-r border-earth-100 h-screen fixed left-0 top-0 flex flex-col thin-scroll overflow-y-auto"
      >
        <div className="px-6 py-6 border-b border-earth-100">
          <img src={LOGO_DARK} alt="Aiyavi Retreat" className="h-9 w-auto object-contain" />
          <div className="text-[10px] uppercase tracking-[0.22em] text-sage-600 mt-2">Admin CMS</div>
        </div>
        <nav className="flex-1 px-3 py-4 flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`sidebar-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive
                    ? "bg-earth-900 text-white"
                    : "text-earth-700 hover:bg-earth-50 hover:text-earth-900"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-earth-100 flex flex-col gap-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="sidebar-preview-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-earth-700 hover:bg-earth-50"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> View website
          </a>
          <button
            onClick={onLogout}
            data-testid="sidebar-logout-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-earth-700 hover:bg-earth-50"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
          {user && (
            <div className="px-2 pt-2 text-[11px] text-earth-500 truncate">
              Signed in as {user.email}
            </div>
          )}
        </div>
      </aside>
      <main className="pl-64 flex-1 min-h-screen">
        <div className="p-8 max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
